import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { formatNumber, formatLakhs } from "../lib/format";
import { exportCSV } from "../lib/csv";
import { Megaphone, RefreshCw, Download, AlertCircle } from "lucide-react";

const DATE_PRESETS = [
  { id: "today",        label: "Today" },
  { id: "yesterday",    label: "Yesterday" },
  { id: "last_7d",      label: "Last 7 days" },
  { id: "last_14d",     label: "Last 14 days" },
  { id: "last_30d",     label: "Last 30 days" },
  { id: "last_90d",     label: "Last 90 days" },
  { id: "this_month",   label: "This month" },
  { id: "last_month",   label: "Last month" },
  { id: "maximum",      label: "Lifetime" },
];

const STATUS_PILL = {
  ACTIVE:       "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  PAUSED:       "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  ARCHIVED:     "bg-zinc-500/10 text-zinc-600 border-zinc-700/20",
  DELETED:      "bg-red-500/15 text-red-400 border-red-500/20",
  COMPLETED:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
};

function inr(n) {
  if (n == null || isNaN(n)) return "—";
  return `₹ ${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function objectiveLabel(obj) {
  if (!obj) return "—";
  return obj.replace(/^OUTCOME_/, "").replace(/_/g, " ").toLowerCase();
}

export default function Ads() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [datePreset, setDatePreset] = useState("last_30d");
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Load accounts on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ads/accounts");
        setAccounts(data.accounts || []);
        const primary = (data.accounts || []).find((a) => a.kind === "owned") || data.accounts?.[0];
        if (primary) setAccountId(primary.id);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  // Reload data when account or date changes
  useEffect(() => {
    if (!accountId) return;
    (async () => {
      setLoading(true); setErr("");
      try {
        const [s, c] = await Promise.all([
          api.get(`/ads/summary?account_id=${accountId}&date_preset=${datePreset}`),
          api.get(`/ads/campaigns?account_id=${accountId}&date_preset=${datePreset}`),
        ]);
        setSummary(s.data && Object.keys(s.data).length ? s.data : null);
        setCampaigns(c.data.campaigns || []);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, [accountId, datePreset]);

  const activeCount = useMemo(() => campaigns.filter((c) => c.effective_status === "ACTIVE").length, [campaigns]);

  const account = accounts.find((a) => a.id === accountId);
  const currency = account?.currency || "INR";

  const onExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    exportCSV(`isf-ads-${datePreset}-${stamp}.csv`, campaigns, [
      { label: "Campaign",     accessor: (c) => c.name },
      { label: "Status",       accessor: (c) => c.effective_status || c.status },
      { label: "Objective",    accessor: (c) => objectiveLabel(c.objective) },
      { label: `Spend (${currency})`, accessor: (c) => c.spend.toFixed(2) },
      { label: "Impressions",  accessor: (c) => c.impressions },
      { label: "Reach",        accessor: (c) => c.reach },
      { label: "Clicks",       accessor: (c) => c.clicks },
      { label: "CTR %",        accessor: (c) => c.ctr.toFixed(3) },
      { label: "CPM",          accessor: (c) => c.cpm.toFixed(2) },
      { label: "CPC",          accessor: (c) => c.cpc.toFixed(2) },
      { label: "Frequency",    accessor: (c) => c.frequency.toFixed(2) },
      { label: "Conversions",  accessor: (c) => c.conversions },
    ]);
  };

  return (
    <Layout>
      <PageHeader
        overline="Meta · paid media"
        title="Ads"
        subtitle="Campaigns running on Meta Ads Manager — performance pulled live via Graph API."
        actions={
          <div className="flex items-center gap-2">
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              data-testid="ad-account-select"
              className="bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name === a.account_id ? `Account ${a.account_id}` : a.name} · {a.currency}
                </option>
              ))}
            </select>
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              data-testid="ad-date-select"
              className="bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
            >
              {DATE_PRESETS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <button
              onClick={onExport}
              disabled={campaigns.length === 0}
              data-testid="ad-export-csv"
              className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] disabled:opacity-40 text-sm px-4 py-2 transition-colors"
            >
              <Download className="h-4 w-4" /> Export
            </button>
          </div>
        }
        testId="ads-header"
      />

      {err && (
        <div className="mb-6 px-4 py-3 text-sm border border-red-500/30 bg-red-500/10 text-red-200 flex items-start gap-2" data-testid="ads-error">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      {/* Account context */}
      {account && (
        <Card className="p-4 mb-6 flex items-center justify-between" testId="ad-account-card">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-emerald-500/10 border border-emerald-500/30 grid place-items-center text-emerald-400">
              <Megaphone className="h-4 w-4" />
            </div>
            <div>
              <div className="overline">{account.kind === "owned" ? "Owned account" : "Client account"}</div>
              <div className="text-sm font-medium">{account.name === account.account_id ? `Account ${account.account_id}` : account.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-right tabular text-xs text-zinc-400">
            <div><div className="overline mb-0.5">Lifetime spend</div><div className="font-display text-white text-base">₹ {Number(account.amount_spent || 0).toLocaleString("en-IN")}</div></div>
            <div><div className="overline mb-0.5">Balance</div><div className="font-display text-white text-base">₹ {Number(account.balance || 0).toLocaleString("en-IN")}</div></div>
            <div><div className="overline mb-0.5">Active campaigns</div><div className="font-display text-emerald-400 text-base">{activeCount}</div></div>
          </div>
        </Card>
      )}

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="p-5" testId="kpi-spend">
            <div className="overline mb-2">Spend</div>
            <div className="font-display text-2xl font-bold tabular">{inr(summary.spend)}</div>
          </Card>
          <Card className="p-5" testId="kpi-impressions">
            <div className="overline mb-2">Impressions</div>
            <div className="font-display text-2xl font-bold tabular">{formatNumber(summary.impressions)}</div>
          </Card>
          <Card className="p-5" testId="kpi-reach">
            <div className="overline mb-2">Reach</div>
            <div className="font-display text-2xl font-bold tabular">{formatNumber(summary.reach)}</div>
          </Card>
          <Card className="p-5" testId="kpi-clicks">
            <div className="overline mb-2">Clicks</div>
            <div className="font-display text-2xl font-bold tabular">{formatNumber(summary.clicks)}</div>
          </Card>
          <Card className="p-5" testId="kpi-ctr">
            <div className="overline mb-2">CTR</div>
            <div className="font-display text-2xl font-bold tabular">{Number(summary.ctr || 0).toFixed(2)}%</div>
            <div className="text-xs text-zinc-500 mt-1">CPM {inr(summary.cpm)} · CPC {inr(summary.cpc)}</div>
          </Card>
          <Card className="p-5" testId="kpi-frequency">
            <div className="overline mb-2">Frequency</div>
            <div className="font-display text-2xl font-bold tabular">{Number(summary.frequency || 0).toFixed(2)}</div>
            <div className="text-xs text-zinc-500 mt-1">avg views per person</div>
          </Card>
        </div>
      )}

      {/* Campaigns table */}
      <Card className="overflow-hidden" testId="campaign-table-card">
        <div className="px-6 py-4 border-b border-zinc-900 flex items-center justify-between">
          <div>
            <div className="overline mb-1">Campaigns</div>
            <div className="font-display text-lg">{campaigns.length} total · {activeCount} active</div>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#181818]">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3 font-medium">Campaign</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Objective</th>
                <th className="px-4 py-3 font-medium text-right">Spend</th>
                <th className="px-4 py-3 font-medium text-right">Impressions</th>
                <th className="px-4 py-3 font-medium text-right">Reach</th>
                <th className="px-4 py-3 font-medium text-right">Clicks</th>
                <th className="px-4 py-3 font-medium text-right">CTR</th>
                <th className="px-4 py-3 font-medium text-right">CPM</th>
                <th className="px-4 py-3 font-medium text-right">CPC</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && !loading && (
                <tr><td colSpan={10} className="px-6 py-10 text-center text-zinc-500 text-sm">
                  No campaigns found for this date range.
                </td></tr>
              )}
              {campaigns.map((c) => {
                const cls = STATUS_PILL[c.effective_status] || STATUS_PILL.PAUSED;
                const hasSpend = c.spend > 0;
                return (
                  <tr key={c.id} className={`border-t border-zinc-900 hover:bg-[#161616] transition-colors ${!hasSpend ? "opacity-60" : ""}`} data-testid={`campaign-row-${c.id}`}>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="truncate" title={c.name}>{c.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] border rounded-sm ${cls}`}>
                        {c.effective_status || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-400 capitalize">{objectiveLabel(c.objective)}</td>
                    <td className="px-4 py-3 text-right tabular text-emerald-400 font-medium">{hasSpend ? inr(c.spend) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular">{formatNumber(c.impressions)}</td>
                    <td className="px-4 py-3 text-right tabular text-zinc-400">{formatNumber(c.reach)}</td>
                    <td className="px-4 py-3 text-right tabular">{formatNumber(c.clicks)}</td>
                    <td className="px-4 py-3 text-right tabular">{c.ctr ? `${c.ctr.toFixed(2)}%` : "—"}</td>
                    <td className="px-4 py-3 text-right tabular text-zinc-400">{c.cpm ? inr(c.cpm) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular text-zinc-400">{c.cpc ? inr(c.cpc) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
